// src/pages/admin/branches/components/BranchTable.jsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../../../hooks/useRTL';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Chip,
    Box,
    Typography,
    Skeleton
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Person as PersonIcon
} from '@mui/icons-material';

// Table skeleton for loading state
const TableRowSkeleton = ({ columns = 7 }) => (
    <TableRow>
        {[...Array(columns)].map((_, index) => (
            <TableCell key={index}>
                <Skeleton variant="text" width="100%" />
            </TableCell>
        ))}
    </TableRow>
);

const BranchTable = ({
    branches,
    loading,
    pagination,
    onPageChange,
    onRowsPerPageChange,
    onEdit,
    onDelete,
    canEdit,
    canDelete
}) => {
    const { t } = useTranslation();
    const { isRTL } = useRTL();

    const getStatusColor = (isActive) => isActive ? 'success' : 'error';
    const getStatusIcon = (isActive) => isActive ? <CheckCircleIcon /> : <CancelIcon />;

    const handlePageChange = (event, newPage) => {
        onPageChange(event, newPage + 1); // Convert from 0-indexed to 1-indexed
    };

    return (
        <>
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>
                                    {t('branches.branchName', 'Branch Name')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>
                                    {t('branches.location', 'Location')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>
                                    {t('branches.contactInfo', 'Contact Info')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>
                                    {t('branches.manager', 'Manager')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>
                                    {t('common.status', 'Status')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>
                                    {t('common.createdAt', 'Created At')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'left' : 'right' }}>
                                    {t('common.actions', 'Actions')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(pagination.limit)].map((_, index) => (
                                    <TableRowSkeleton key={`skeleton-${index}`} columns={7} />
                                ))
                            ) : branches.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
                                            {t('branches.noBranchesFound', 'No branches found')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                branches.map((branch) => (
                                    <TableRow key={branch._id} hover>
                                        {/* Branch Name */}
                                        <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {branch.name}
                                            </Typography>
                                        </TableCell>

                                        {/* Location */}
                                        <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            <Box display="flex" alignItems="flex-start" gap={0.5}>
                                                <LocationIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                                                <Box>
                                                    <Typography variant="body2">
                                                        {branch.location}
                                                    </Typography>
                                                    {branch.address && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {branch.address}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        {/* Contact Info */}
                                        <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {branch.phone ? (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <PhoneIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">
                                                        {branch.phone}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* Manager */}
                                        <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {branch.manager ? (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <PersonIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">
                                                        {branch.manager}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('branches.notAssigned', 'Not assigned')}
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            <Chip
                                                icon={getStatusIcon(branch.isActive)}
                                                label={branch.isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                                                color={getStatusColor(branch.isActive)}
                                                size="small"
                                                variant={branch.isActive ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>

                                        {/* Created At */}
                                        <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            <Typography variant="body2">
                                                {new Date(branch.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>

                                        {/* Actions */}
                                        <TableCell sx={{ textAlign: isRTL ? 'left' : 'right' }}>
                                            <Box display="flex" gap={0.5} justifyContent={isRTL ? 'flex-start' : 'flex-end'}>
                                                {canEdit && (
                                                    <Tooltip title={t('common.edit', 'Edit')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onEdit(branch)}
                                                            color="primary"
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title={t('common.delete', 'Delete')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onDelete(branch)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper sx={{ mt: 5 }}>
                {/* Pagination */}
                {!loading && pagination.total > 0 && (
                    <TablePagination
                        component="div"
                        count={pagination.total}
                        page={pagination.page - 1}
                        onPageChange={handlePageChange}
                        rowsPerPage={pagination.limit}
                        onRowsPerPageChange={onRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage={t('common.rowsPerPage', 'Rows per page')}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} ${t('common.of', 'of')} ${count}`
                        }
                    />
                )}
            </Paper>
        </>
    );
};

export default BranchTable;